/**
 * interior_lift — THE LIFT CAB, pure (the interiors programme, slice L, 2026-10-01).
 *
 * `building_interiors.interiorSpec` cuts the shaft (a 1.6 m cell on every storey, enclosed, filled by a collide-only
 * PLUG per storey so the shell is airtight by itself). This file is everything about the car that rides in it, with no
 * three.js, no DOM and no clock of its own:
 *
 *   · THE SHAFT FRAME    `u` runs along the door wall, `v` from the door plane toward the perimeter wall — one frame
 *                        for a west-wall shaft (door east) and a back-wall shaft (door south).
 *   · THE WALK RASTER    `walkRaster`: where the REAL capsule (radius 0.3 m) can stand on a storey, from the shell's
 *                        own colliders and the furniture that is drawn. `reaches` floods it.
 *   · THE SERVICE RULE   `liftService`: which storeys the lift serves and why not — a storey is served when nothing
 *                        drawn stands in the shaft or on its landing and a body can stand at the call post; the lift
 *                        RUNS when two storeys are served and one of them can be walked to from its own way in.
 *   · THE RIDE           `rideProfile`: an eased run (2 m/s, 1.4 m/s² in and out) in closed form over fixed ticks — the
 *                        same height on every tick for the same call, arriving EXACTLY on the storey.
 *   · THE STATE MACHINE  `liftStep`: idle · opening · open · closing · travel, fed the capsule's place in the shaft
 *                        frame every fixed tick. THE DOOR SENSOR: a door never closes with a body in the doorway, a
 *                        door never closes on a rider who chose no storey, and the car never moves unless its doors
 *                        are shut. `liftSkip` ends any wait at once (the lore says the lifts are temperamental; the
 *                        game never makes a player wait for one).
 *   · THE PLUG RULE      `plugsOpen`: which storeys' plugs are out — the docked storey while its doors are not shut,
 *                        every storey a RIDER crosses while crossing; an empty car moves through a solid shaft.
 *   · THE DRAWN PARTS    the cab's boxes, the landing door's leaves, the call plate's seat, and the boxes of the
 *                        visit's own static lift fixtures (which the lift module takes over).
 *
 * The car is CLIENT-LOCAL PRESENTATION: every player rides their own. Walking never writes Y on the server and a ride
 * moves nothing in XZ, so the row stays where the rider stands and reconcile has nothing to correct.
 */
import { FLOOR_H, LIFT_SHAFT_M, LIFT_APRON_M, LIFT_SIDE_WALL_M, LIFT_DOOR_H, type InteriorSpec, type LiftShaft, type IntBox } from './building_interiors.js';

// ─── Constants ───────────────────────────────────────────────────────────────────────────────────────────────────────

/** The fixed step the machine is written in (the engine's FixedSim). */
export const LIFT_HZ = 60;
export const LIFT = {
  /** A rider's run: top speed and the ease in / out. One storey (3.4 m) takes 3.1 s, seven take 13.3 s. */
  SPEED_MPS: 2.0, ACCEL_MPS2: 1.4,
  /** An EMPTY car answering a call does not make anyone wait. */
  EXPRESS_MPS: 7.0, EXPRESS_ACCEL_MPS2: 9.0,
  /** Doors: ticks to open or close (0.8 s). */
  DOOR_TICKS: 48,
  /** How long an empty car holds its doors open at a landing (5 s), and after its caller is aboard (0.5 s). */
  DWELL_TICKS: 300, BOARD_TICKS: 30,
  /** The door sensor: a capsule centre within this of the door plane (either side) holds the doors. */
  SENSE_M: 0.36,
  /** How far a body may be from the car's floor (m) and still count as at it. */
  LEVEL_M: 0.9,
  /** Ticks the platform holds a rider after arrival while the floor under the feet becomes solid again. */
  SETTLE_TICKS: 4,
} as const;

/** The capsule the avatar is (PhysicsModule): radius, and the skin the walk raster adds. */
export const BODY_R = 0.3, BODY_SKIN = 0.02;
/** The walking band (m above a storey's floor): what a body steps over, and what clears its head — the planner's. */
export const WALK_STEP = 0.4, WALK_HEAD = 2.0;
/** The walk raster's cell (m). */
export const WALK_CELL = 0.1;

/** A storey's floor height above the building's ground (the ground floor's finish slab is 8 cm). */
export function floorY(f: number): number { return f === 0 ? 0.08 : f * FLOOR_H; }
/** The storey a height belongs to (the nearest floor at or below the feet, a step's tolerance). */
export function floorAtHeight(y: number, floors: number): number {
  return Math.max(0, Math.min(floors - 1, Math.floor((y + 0.6) / FLOOR_H)));
}
/** A storey's name on the picker and the prompt. */
export function floorLabel(f: number): string { return f === 0 ? 'G' : String(f); }
export function floorName(f: number): string { return f === 0 ? 'Ground floor' : `Floor ${f}`; }

// ─── The shaft frame ─────────────────────────────────────────────────────────────────────────────────────────────────

type Shaft = Pick<LiftShaft, 'rect' | 'wall' | 'door'>;
/** (u, v) → building-local (x, z). */
export function shaftToLocal(s: Shaft, u: number, v: number): { x: number; z: number } {
  return s.wall === 'west' ? { x: s.rect.x1 - v, z: s.rect.z0 + u } : { x: s.rect.x0 + u, z: s.rect.z0 + v };
}
/** Building-local (x, z) → (u, v). */
export function localToShaft(s: Shaft, x: number, z: number): { u: number; v: number } {
  return s.wall === 'west' ? { u: z - s.rect.z0, v: s.rect.x1 - x } : { u: x - s.rect.x0, v: z - s.rect.z0 };
}
/** The landing: the apron the planner keeps clear before the door. */
export function apronRect(s: Shaft): { x0: number; x1: number; z0: number; z1: number } {
  const r = s.rect;
  return s.wall === 'west' ? { x0: r.x1, x1: r.x1 + LIFT_APRON_M, z0: r.z0, z1: r.z1 } : { x0: r.x0, x1: r.x1, z0: r.z0 - LIFT_APRON_M, z1: r.z0 };
}
/** The yaw (three's `rotation.y`, a +Z-facing model) that looks OUT of the door into the room. */
export function doorOutYaw(s: Shaft): number { return s.wall === 'west' ? Math.PI / 2 : Math.PI; }
/** An axis-aligned box given in the shaft frame → building-local centre and size. */
export function shaftBox(s: Shaft, u0: number, u1: number, v0: number, v1: number): { x: number; z: number; w: number; d: number } {
  const a = shaftToLocal(s, u0, v0), b = shaftToLocal(s, u1, v1);
  return { x: (a.x + b.x) / 2, z: (a.z + b.z) / 2, w: Math.abs(b.x - a.x), d: Math.abs(b.z - a.z) };
}

const S = LIFT_SHAFT_M, SW = LIFT_SIDE_WALL_M;
/** Where the capsule's CENTRE may be inside the car (shaft frame): clear of the side walls by a body, clear of the door
 *  plane by the sensor, and inside what `clampIndoor` calls legal (0.5 m off the perimeter wall) — so the indoor
 *  rescue and the reconcile clamp never have anything to say about a rider. */
export const CAB_INNER = { u0: SW + BODY_R + BODY_SKIN, u1: S - SW - BODY_R - BODY_SKIN, v0: LIFT.SENSE_M + 0.04, v1: S - 0.55 } as const;

/** The platform a rider is held on: its centre (building-local) and half extents on the building's axes. */
export function cabRideRect(s: Shaft): { x: number; z: number; hx: number; hz: number } {
  const b = shaftBox(s, CAB_INNER.u0, CAB_INNER.u1, CAB_INNER.v0, CAB_INNER.v1);
  return { x: b.x, z: b.z, hx: b.w / 2, hz: b.d / 2 };
}

// ─── What the machine is told about the body ─────────────────────────────────────────────────────────────────────────

export interface LiftSense { inCab: boolean; inDoorway: boolean }
/** Where a body (building-local x / z, feet height above the building's ground) is, against a car whose floor is at `cabY`. */
export function senseBody(s: Shaft, x: number, z: number, y: number, cabY: number): LiftSense {
  const { u, v } = localToShaft(s, x, z);
  const level = Math.abs(y - cabY) <= LIFT.LEVEL_M;
  const span = u > SW - 0.12 && u < S - SW + 0.12;
  return {
    inDoorway: level && span && Math.abs(v) < LIFT.SENSE_M,
    inCab: level && u > 0 && u < S && v >= LIFT.SENSE_M && v < S,
  };
}

// ─── The walk raster ─────────────────────────────────────────────────────────────────────────────────────────────────

export interface WalkSolid { floor: number; x0: number; x1: number; z0: number; z1: number }
export interface WalkRaster {
  floor: number; nx: number; nz: number; hw: number; hd: number;
  /** 1 = a body's centre cannot be here. */
  blocked: Uint8Array;
}
const cellOf = (r: WalkRaster, x: number, z: number): number => {
  const i = Math.floor((x + r.hw) / WALK_CELL), j = Math.floor((z + r.hd) / WALK_CELL);
  return i < 0 || j < 0 || i >= r.nx || j >= r.nz ? -1 : j * r.nx + i;
};
/**
 * Where the real capsule's centre can be on storey `f`: over a floor (above the ground storey: a slab or a landing),
 * and a body's radius clear of every shell collider in the walking band and of every drawn walk-blocking piece.
 * `open` = shell collide boxes to leave out (the docked storey's plug, when asking about a car standing open).
 */
export function walkRaster(spec: Pick<InteriorSpec, 'hw' | 'hd' | 'boxes'>, f: number, solids: ReadonlyArray<WalkSolid> = [], open?: (b: IntBox) => boolean): WalkRaster {
  const nx = Math.max(1, Math.ceil((2 * spec.hw) / WALK_CELL)), nz = Math.max(1, Math.ceil((2 * spec.hd) / WALK_CELL));
  const r: WalkRaster = { floor: f, nx, nz, hw: spec.hw, hd: spec.hd, blocked: new Uint8Array(nx * nz) };
  const y0 = floorY(f), R = BODY_R + BODY_SKIN;
  const cx = (i: number): number => -spec.hw + (i + 0.5) * WALK_CELL, cz = (j: number): number => -spec.hd + (j + 0.5) * WALK_CELL;
  const span = (lo: number, hi: number, half: number, n: number): [number, number] =>
    [Math.max(0, Math.ceil((lo + half) / WALK_CELL - 0.5)), Math.min(n - 1, Math.floor((hi + half) / WALK_CELL - 0.5))];
  if (f >= 1) {
    r.blocked.fill(1);
    for (const b of spec.boxes) {
      // anything solid whose top IS this storey's level is floor: a slab strip, the stair landing — and, in the lift's
      // cell, the plug of the storey below (the floor a docked car's rider stands on)
      if (!b.collide || (open && open(b))) continue;
      if (Math.abs(b.y + b.h / 2 - y0) > 0.06) continue;
      const [i0, i1] = span(b.x - b.w / 2, b.x + b.w / 2, spec.hw, nx), [j0, j1] = span(b.z - b.d / 2, b.z + b.d / 2, spec.hd, nz);
      for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) r.blocked[j * nx + i] = 0;
    }
  }
  const block = (x0: number, x1: number, z0: number, z1: number): void => {
    const [i0, i1] = span(x0 - R, x1 + R, spec.hw, nx), [j0, j1] = span(z0 - R, z1 + R, spec.hd, nz);
    for (let j = j0; j <= j1; j++) {
      const z = cz(j), dz = z < z0 ? z0 - z : z > z1 ? z - z1 : 0;
      for (let i = i0; i <= i1; i++) {
        const x = cx(i), dx = x < x0 ? x0 - x : x > x1 ? x - x1 : 0;
        if (dx * dx + dz * dz < R * R) r.blocked[j * nx + i] = 1;
      }
    }
  };
  for (const b of spec.boxes) {
    if (!b.collide || (open && open(b))) continue;
    const top = b.y + b.h / 2, bottom = b.y - b.h / 2;
    if (top <= y0 + WALK_STEP || bottom >= y0 + WALK_HEAD) continue;
    block(b.x - b.w / 2, b.x + b.w / 2, b.z - b.d / 2, b.z + b.d / 2);
  }
  for (const s of solids) if (s.floor === f) block(s.x0, s.x1, s.z0, s.z1);
  return r;
}
/** Can a body stand at this point? */
export function standable(r: WalkRaster, x: number, z: number): boolean { const k = cellOf(r, x, z); return k >= 0 && !r.blocked[k]; }
/** The free cell nearest a point within `maxM` (−1 = none). */
export function nearestFreeCell(r: WalkRaster, x: number, z: number, maxM = 0.5): number {
  const k = cellOf(r, x, z);
  if (k >= 0 && !r.blocked[k]) return k;
  const n = Math.ceil(maxM / WALK_CELL), ci = Math.floor((x + r.hw) / WALK_CELL), cj = Math.floor((z + r.hd) / WALK_CELL);
  let best = -1, bd = Infinity;
  for (let dj = -n; dj <= n; dj++) for (let di = -n; di <= n; di++) {
    const i = ci + di, j = cj + dj;
    if (i < 0 || j < 0 || i >= r.nx || j >= r.nz || r.blocked[j * r.nx + i]) continue;
    const d2 = di * di + dj * dj;
    if (d2 < bd) { bd = d2; best = j * r.nx + i; }
  }
  return best >= 0 && Math.sqrt(bd) * WALK_CELL <= maxM ? best : -1;
}
/** Is there a walk (4-connected) from one point to another? */
export function reaches(r: WalkRaster, ax: number, az: number, bx: number, bz: number): boolean {
  const a = nearestFreeCell(r, ax, az), b = nearestFreeCell(r, bx, bz);
  if (a < 0 || b < 0) return false;
  if (a === b) return true;
  const seen = new Uint8Array(r.blocked.length), q = new Int32Array(r.blocked.length);
  let h = 0, t = 0;
  q[t++] = a; seen[a] = 1;
  while (h < t) {
    const k = q[h++], i = k % r.nx, j = (k - i) / r.nx;
    for (let d = 0; d < 4; d++) {
      const ni = i + (d === 0 ? 1 : d === 1 ? -1 : 0), nj = j + (d === 2 ? 1 : d === 3 ? -1 : 0);
      if (ni < 0 || nj < 0 || ni >= r.nx || nj >= r.nz) continue;
      const n = nj * r.nx + ni;
      if (seen[n] || r.blocked[n]) continue;
      if (n === b) return true;
      seen[n] = 1; q[t++] = n;
    }
  }
  return false;
}
/** Is the straight walk from a to b free the whole way (sampled every half cell)? */
export function segmentWalkable(r: WalkRaster, ax: number, az: number, bx: number, bz: number): boolean {
  const len = Math.hypot(bx - ax, bz - az), n = Math.max(1, Math.ceil(len / (WALK_CELL / 2)));
  for (let i = 0; i <= n; i++) {
    const k = cellOf(r, ax + ((bx - ax) * i) / n, az + ((bz - az) * i) / n);
    if (k < 0 || r.blocked[k]) return false;
  }
  return true;
}

/** Where a storey is walked INTO: the door on the ground, the top of the stair run above it. */
export function floorRoot(spec: Pick<InteriorSpec, 'hd' | 'stair'>, f: number): { x: number; z: number } {
  return f === 0 ? { x: 0, z: -spec.hd + 0.9 } : { x: spec.stair.x, z: spec.stair.z + spec.stair.d / 2 + 0.3 };
}

// ─── The service rule ────────────────────────────────────────────────────────────────────────────────────────────────

export type FloorRefusal = '' | 'furniture stands in the shaft' | 'furniture stands on the landing' | 'no room to stand at the call post';
export interface LiftService {
  /** Does the lift run at all. */
  runs: boolean;
  /** The storeys it stops at (ascending). */
  served: number[];
  floors: Array<{ floor: number; served: boolean; why: FloorRefusal; walkIn: boolean }>;
  /** Why it does not run ('' when it does). */
  why: string;
}
const meets = (a: { x0: number; x1: number; z0: number; z1: number }, b: { x0: number; x1: number; z0: number; z1: number }): boolean =>
  a.x0 < b.x1 - 1e-6 && a.x1 > b.x0 + 1e-6 && a.z0 < b.z1 - 1e-6 && a.z1 > b.z0 + 1e-6;

/**
 * Which storeys the lift serves. A storey is served when no drawn walk-blocking piece stands in the shaft's cell or on
 * its landing and a body can stand at the call post. The lift RUNS when at least two storeys are served and at least
 * one of them can be walked to from its own way in (the door, the stair top) — the lift is itself the way to the others.
 */
export function liftService(spec: Pick<InteriorSpec, 'floors' | 'hw' | 'hd' | 'boxes' | 'stair' | 'lift'>, solids: ReadonlyArray<WalkSolid> = [], walls: ReadonlyArray<WalkSolid> = []): LiftService {
  const shaft = spec.lift;
  if (!shaft) return { runs: false, served: [], floors: [], why: spec.floors < 3 ? 'under three floors' : 'this shell has no shaft' };
  const apron = apronRect(shaft);
  const out: LiftService = { runs: false, served: [], floors: [], why: '' };
  let walkIn = false;
  for (let f = 0; f < spec.floors; f++) {
    const here = solids.filter((s) => s.floor === f);
    let why: FloorRefusal = '';
    // (a piece planned flush against the planner's reserve may reach `SHAFT_FRAME_TOL_M` into the cell or the apron —
    // the deed frame; what matters beyond that is PHYSICAL and tested below: a body at the call post, the walk to it)
    if (shaftConflict(shaft, here)) why = 'furniture stands in the shaft';
    else if (here.some((s) => meets(s, { x0: apron.x0 + SHAFT_FRAME_TOL_M, x1: apron.x1 - SHAFT_FRAME_TOL_M, z0: apron.z0 + SHAFT_FRAME_TOL_M, z1: apron.z1 - SHAFT_FRAME_TOL_M }))) why = 'furniture stands on the landing';
    let reach = false;
    if (!why) {
      // `walls` = the room partitions' solid spans (the rooms revision): they block a body like any piece, but a wall
      // that ABUTS the cell is no "furniture in the shaft" — so they enter only the physical tests
      const r = walkRaster(spec, f, walls.length ? [...solids, ...walls] : solids);
      if (!standable(r, shaft.call.x, shaft.call.z)) why = 'no room to stand at the call post';
      else if (!walkIn) { const root = floorRoot(spec, f); reach = reaches(r, root.x, root.z, shaft.call.x, shaft.call.z); }
    }
    if (!why) out.served.push(f);
    if (reach) walkIn = true;
    out.floors.push({ floor: f, served: !why, why, walkIn: reach });
  }
  if (out.served.length < 2) out.why = `only ${out.served.length} of ${spec.floors} landings ${out.served.length === 1 ? 'is' : 'are'} clear`;
  else if (!walkIn) out.why = 'no served landing can be walked to';
  out.runs = !out.why;
  return out;
}
/** The plan stands furniture in the shaft's own cell on some storey — the shell is cut WITHOUT a shaft there. */
/** How far a planned piece may reach into the cell before it "stands in the shaft". From the rooms revision on a
 *  deed's room is PLANNED in the shared footprint table's box while its shell is cut from the measured model
 *  (`interior_rooms.DEED_BOX_TOLERANCE_M` 0.08 m apart at most): the planner's reserve and the shell's cell can be
 *  ~5 cm apart, and a piece planned flush against the reserve must not cost the building its lift. */
export const SHAFT_FRAME_TOL_M = 0.06;
export function shaftConflict(shaft: Shaft, solids: ReadonlyArray<WalkSolid>): boolean {
  const r = shaft.rect, t = SHAFT_FRAME_TOL_M;
  const core = { x0: r.x0 + t, x1: r.x1 - t, z0: r.z0 + t, z1: r.z1 - t };
  return solids.some((s) => meets(s, core));
}

// ─── The ride ────────────────────────────────────────────────────────────────────────────────────────────────────────

export interface RideProfile { distM: number; ticks: number; seconds: number; at(tick: number): number }
/** An eased run over `distM`: ease in at `accel`, cruise at `vmax`, ease out — in closed form, so tick t is tick t on
 *  every machine, and the last tick is EXACTLY the distance. */
export function rideProfile(distM: number, vmax: number = LIFT.SPEED_MPS, accel: number = LIFT.ACCEL_MPS2): RideProfile {
  const d = Math.max(0, distM);
  if (d < 1e-6) return { distM: 0, ticks: 0, seconds: 0, at: () => 0 };
  let ta = vmax / accel, da = 0.5 * accel * ta * ta;
  let total: number;
  if (2 * da >= d) { ta = Math.sqrt(d / accel); da = d / 2; total = 2 * ta; } else total = 2 * ta + (d - 2 * da) / vmax;
  const peak = accel * ta;
  const ticks = Math.max(1, Math.ceil(total * LIFT_HZ - 1e-9));
  const at = (tick: number): number => {
    if (tick >= ticks) return d;
    const t = Math.max(0, tick) / LIFT_HZ;
    if (t < ta) return 0.5 * accel * t * t;
    if (t < total - ta) return da + peak * (t - ta);
    const r = total - t;
    return Math.max(0, d - 0.5 * accel * r * r);
  };
  return { distM: d, ticks, seconds: ticks / LIFT_HZ, at };
}
/** A rider's time between two storeys, doors included (close, run, open) — what the residents' itinerary charges. */
export function liftTripS(from: number, to: number): number {
  return (2 * LIFT.DOOR_TICKS) / LIFT_HZ + rideProfile(Math.abs(floorY(to) - floorY(from))).seconds;
}

// ─── The state machine ───────────────────────────────────────────────────────────────────────────────────────────────

export type LiftPhase = 'idle' | 'opening' | 'open' | 'closing' | 'travel';
export type RideMode = 'ride' | 'express' | 'instant';
export interface LiftState {
  phase: LiftPhase;
  /** The storey the car is docked at; while it travels, the one it left. */
  floor: number;
  /** Where it is going (= `floor` when it is not). */
  target: number;
  /** The car's floor, m above the building's ground. */
  y: number;
  /** 0 shut … 1 open (the docked landing's doors). */
  door: number;
  /** Ticks in the phase. */
  t: number;
  /** A landing asked for the car (−1 none). */
  call: number;
  /** The storey the player chose (−1 none): taken once the player is aboard and the doors are shut. */
  dest: number;
  /** The player rides (travel only): the platform holds them. */
  rider: boolean;
  mode: RideMode;
  /** Ticks left before an empty open car closes; ticks a boarded rider has stood inside. */
  dwell: number; boarded: number;
  /** The travel in progress. */
  fromY: number; toY: number; ticks: number;
  /** Rides are instant (VR teleport comfort; the reduced-motion preference). */
  instantRides: boolean;
}
export type LiftEvent = 'opening' | 'opened' | 'closing' | 'closed' | 'reopen' | 'depart' | 'arrive';

export function liftInit(floor = 0): LiftState {
  return { phase: 'idle', floor, target: floor, y: floorY(floor), door: 0, t: 0, call: -1, dest: -1, rider: false, mode: 'ride', dwell: 0, boarded: 0, fromY: floorY(floor), toY: floorY(floor), ticks: 0, instantRides: false };
}
/** A landing calls the car (and may name where its caller wants to go once aboard). */
export function liftCall(s: LiftState, served: ReadonlyArray<number>, at: number, dest = -1): boolean {
  if (!served.includes(at)) return false;
  s.call = at;
  s.dest = dest >= 0 && dest !== at && served.includes(dest) ? dest : -1;
  if (s.phase === 'open' && s.floor === at) s.dwell = LIFT.DWELL_TICKS;
  return true;
}
/** The rider (aboard a docked car) chooses a storey. */
export function liftSelect(s: LiftState, served: ReadonlyArray<number>, dest: number): boolean {
  if (!served.includes(dest) || s.phase === 'travel') return false;
  if (dest === s.floor) { s.dest = -1; return true; }
  s.dest = dest; s.boarded = 0;
  return true;
}
/** End the current wait NOW: doors finish, an answering car arrives, a ride arrives. Never refused. */
export function liftSkip(s: LiftState): void {
  if (s.phase === 'opening') s.door = 1;
  else if (s.phase === 'closing') s.door = Math.min(s.door, 1 / LIFT.DOOR_TICKS);
  else if (s.phase === 'travel') s.t = s.ticks;
  else if (s.phase === 'open') { s.boarded = LIFT.BOARD_TICKS; if (s.dest < 0) s.dwell = Math.min(s.dwell, 1); }
  else if (s.phase === 'idle' && s.call >= 0 && s.call !== s.floor) s.mode = 'instant';
}
function depart(s: LiftState, target: number, rider: boolean, ev: LiftEvent[]): void {
  s.phase = 'travel'; s.target = target; s.t = 0; s.rider = rider;
  s.fromY = s.y; s.toY = floorY(target);
  const dist = Math.abs(s.toY - s.fromY);
  if (s.mode !== 'instant') s.mode = rider ? 'ride' : 'express';
  if (rider && s.instantRides) s.mode = 'instant';
  s.ticks = s.mode === 'instant' ? 1 : s.mode === 'express' ? rideProfile(dist, LIFT.EXPRESS_MPS, LIFT.EXPRESS_ACCEL_MPS2).ticks : rideProfile(dist).ticks;
  ev.push('depart');
}
/**
 * One fixed tick. `sense` is where the player's body is against the car (false / false when it is nowhere near).
 * The laws, in the order they are checked:
 *   · a car moves only with its doors shut (`travel` is entered from `closing` at door 0, or from `idle`);
 *   · doors never close on a body in the doorway (they re-open), and never on a rider who chose no storey;
 *   · a rider is taken only from INSIDE the car — a body in the doorway is never aboard.
 */
export function liftStep(s: LiftState, sense: LiftSense): LiftEvent[] {
  const ev: LiftEvent[] = [];
  s.t++;
  switch (s.phase) {
    case 'idle': {
      if (sense.inCab || sense.inDoorway) { s.phase = 'opening'; s.t = 0; ev.push('opening'); break; } // never shut on a body
      if (s.call >= 0) {
        if (s.call === s.floor) { s.phase = 'opening'; s.t = 0; ev.push('opening'); }
        else depart(s, s.call, false, ev);
      }
      break;
    }
    case 'opening': {
      s.door = Math.min(1, s.door + 1 / LIFT.DOOR_TICKS);
      if (s.door >= 1) { s.phase = 'open'; s.t = 0; s.dwell = LIFT.DWELL_TICKS; s.boarded = 0; if (s.call === s.floor) s.call = -1; ev.push('opened'); }
      break;
    }
    case 'open': {
      if (sense.inCab) {
        s.dwell = LIFT.DWELL_TICKS;
        if (s.dest >= 0 && s.dest !== s.floor && !sense.inDoorway) { if (++s.boarded >= LIFT.BOARD_TICKS) { s.phase = 'closing'; s.t = 0; ev.push('closing'); } }
        else s.boarded = 0;
      } else if (sense.inDoorway) { s.dwell = LIFT.DWELL_TICKS; s.boarded = 0; }
      else {
        s.boarded = 0;
        if (--s.dwell <= 0) { s.dest = -1; s.phase = 'closing'; s.t = 0; ev.push('closing'); } // nobody came: the choice lapses with the wait
      }
      break;
    }
    case 'closing': {
      if (sense.inDoorway) { s.phase = 'opening'; s.t = 0; ev.push('reopen'); break; }
      s.door = Math.max(0, s.door - 1 / LIFT.DOOR_TICKS);
      if (s.door <= 0) {
        ev.push('closed');
        if (sense.inCab && s.dest >= 0 && s.dest !== s.floor) { const to = s.dest; s.dest = -1; s.call = -1; s.mode = 'ride'; depart(s, to, true, ev); }
        else if (sense.inCab) { s.phase = 'opening'; s.t = 0; ev.push('reopen'); } // a rider with nowhere to go is never shut in
        else { s.phase = 'idle'; s.t = 0; s.mode = 'ride'; }
      }
      break;
    }
    case 'travel': {
      const p = s.mode === 'instant' ? null : s.mode === 'express' ? rideProfile(Math.abs(s.toY - s.fromY), LIFT.EXPRESS_MPS, LIFT.EXPRESS_ACCEL_MPS2) : rideProfile(Math.abs(s.toY - s.fromY));
      if (s.t >= s.ticks || !p) {
        s.y = s.toY; s.floor = s.target; s.phase = 'opening'; s.t = 0; s.mode = 'ride';
        if (s.call === s.floor) s.call = -1;
        ev.push('arrive', 'opening');
      } else s.y = s.fromY + Math.sign(s.toY - s.fromY) * p.at(s.t);
      break;
    }
  }
  return ev;
}
/** The storey whose floor is nearest the car (where a ride that must end, ends). */
export function nearestServed(served: ReadonlyArray<number>, y: number): number {
  let best = served.length ? served[0] : 0, bd = Infinity;
  for (const f of served) { const d = Math.abs(floorY(f) - y); if (d < bd) { bd = d; best = f; } }
  return best;
}
/** End whatever the car is doing at the storey nearest it, doors opening (a ride that must stop: the visit's plan
 *  changed, the lever, the module going away). The rider — if any — is at a floor. */
export function liftAbort(s: LiftState, served: ReadonlyArray<number>): number {
  const f = nearestServed(served.length ? served : [s.floor], s.y);
  s.floor = f; s.target = f; s.y = floorY(f); s.phase = 'opening'; s.t = 0; s.call = -1; s.dest = -1; s.mode = 'ride';
  return f;
}

/**
 * THE PLUG RULE: the storeys whose shaft plug is OUT this tick. The docked storey while its doors are not shut (the
 * car's floor is then the plug BELOW it — 3.4 m of solid — or the ground slab); every storey a rider crosses, while
 * the platform holds them; nothing else, ever — an empty car moves through a solid shaft.
 */
export function plugsOpen(s: LiftState, floors: number, settling = false): number[] {
  const out: number[] = [];
  if (s.phase === 'travel') {
    if (!s.rider) return out;
    const a = floorAtHeight(Math.min(s.fromY, s.toY), floors), b = floorAtHeight(Math.max(s.fromY, s.toY), floors);
    for (let f = a; f <= b; f++) out.push(f);
    return out;
  }
  if (s.door > 0 || s.phase === 'opening' || s.phase === 'open' || settling) out.push(s.floor);
  return out;
}

// ─── The car as a whole: the machine, the platform and the plugs, in one step ────────────────────────────────────────

/** What the car needs of the world (the lift module gives it PhysicsModule; the gate gives it the same). */
export interface LiftHost {
  /** Put a storey's shaft plug back (`solid`) or take it out. */
  plug(floor: number, solid: boolean): void;
  /** Hold the rider's feet on the car's floor at `y` (m above the building's ground); null lets go. */
  ride(y: number | null): void;
  /** Is the platform still holding the rider (it lets go by itself when the body is taken off it)? */
  riding(): boolean;
}
export interface LiftRun {
  state: LiftState; floors: number;
  /** The storeys whose plug is out right now. */
  plugOut: Set<number>;
  /** The platform holds a rider; ticks left of the hold after arrival. */
  riding: boolean; settle: number;
  /** Rides that lost their rider (the platform let go mid-run). */
  lost: number;
}
export function liftRunInit(floors: number, floor = 0): LiftRun {
  return { state: liftInit(floor), floors, plugOut: new Set(), riding: false, settle: 0, lost: 0 };
}
function syncPlugs(run: LiftRun, host: LiftHost): void {
  const want = new Set(plugsOpen(run.state, run.floors, run.settle > 0));
  for (const f of [...run.plugOut]) if (!want.has(f)) { host.plug(f, true); run.plugOut.delete(f); }
  for (const f of want) if (!run.plugOut.has(f)) { host.plug(f, false); run.plugOut.add(f); }
}
/**
 * One fixed tick of the whole car, BEFORE the avatar's own step: the machine (when `stepping`), then the platform,
 * then the plugs. THE ORDER IS THE SAFETY: the platform takes the rider on the tick the car departs (so the plugs
 * it crosses can go), holds them `SETTLE_TICKS` after it arrives (while the plug under their feet becomes solid again),
 * and only then lets go.
 */
export function liftRunStep(run: LiftRun, sense: LiftSense, host: LiftHost, stepping = true): LiftEvent[] {
  const st = run.state;
  const events = stepping ? liftStep(st, sense) : [];
  if (events.includes('arrive') && st.rider) run.settle = LIFT.SETTLE_TICKS;
  if (st.phase === 'travel' && st.rider) {
    if (run.riding && !host.riding()) { run.riding = false; st.rider = false; run.lost++; } // the body was taken off the car: it runs on empty
    else { host.ride(st.y); run.riding = true; }
  } else if (run.settle > 0) {
    host.ride(st.y);
    run.riding = true;
    if (--run.settle === 0) { host.ride(null); run.riding = false; st.rider = false; }
  } else if (run.riding) { host.ride(null); run.riding = false; st.rider = false; }
  syncPlugs(run, host);
  return events;
}
/** The room changed under a car in use (or the car must stop): it ends at the nearest storey it still serves, doors
 *  opening; a rider is held there while the floor under them comes back. Returns the storey. */
export function liftRunAbort(run: LiftRun, served: ReadonlyArray<number>, host: LiftHost): number {
  const all = [...Array(run.floors).keys()];
  const f = liftAbort(run.state, served.length ? served : all);
  if (run.riding) { host.ride(run.state.y); run.settle = LIFT.SETTLE_TICKS; }
  syncPlugs(run, host);
  return f;
}
/** The car goes away while the shell stays (the lever, the module): every plug back, the platform released. The caller
 *  stands a body that is inside the open car on the landing FIRST. */
export function liftRunStop(run: LiftRun, host: LiftHost): void {
  if (run.riding) { host.ride(null); run.riding = false; }
  run.state.rider = false; run.settle = 0;
  for (const f of [...run.plugOut]) host.plug(f, true);
  run.plugOut.clear();
}

// ─── The drawn parts ─────────────────────────────────────────────────────────────────────────────────────────────────

export type LiftRole = 'frame' | 'cab' | 'door' | 'call' | 'ring';
export interface LiftPart { x: number; y: number; z: number; w: number; h: number; d: number; role: LiftRole }
/** The car: a floor, three walls, a ceiling with its light, a hand rail. `y` is measured from the car's floor. */
export function cabParts(s: Shaft): LiftPart[] {
  const at = (u0: number, u1: number, v0: number, v1: number, y0: number, y1: number, role: LiftRole): LiftPart => ({ ...shaftBox(s, u0, u1, v0, v1), y: (y0 + y1) / 2, h: y1 - y0, role });
  const a = SW + 0.01, b = S - SW - 0.01, back = S - 0.12;
  return [
    at(a, b, 0.07, back, -0.05, 0.012, 'frame'),            // the floor plate (a hair proud of the slab it docks at)
    at(a, b, back - 0.03, back, 0.012, 2.34, 'cab'),        // the back panel
    at(a, a + 0.03, 0.07, back, 0.012, 2.34, 'cab'),        // side panels
    at(b - 0.03, b, 0.07, back, 0.012, 2.34, 'cab'),
    at(a, b, 0.07, back, 2.34, 2.4, 'frame'),               // the ceiling
    at(a + 0.25, b - 0.25, 0.45, back - 0.35, 2.325, 2.34, 'call'), // its light
    at(a + 0.03, a + 0.07, 0.3, back - 0.15, 0.92, 0.97, 'frame'),  // hand rails
    at(b - 0.07, b - 0.03, 0.3, back - 0.15, 0.92, 0.97, 'frame'),
  ];
}
/** One landing door leaf at an open fraction: it gathers into its jamb. `y` from the storey's floor. */
export function leafPart(s: Shaft, leaf: 0 | 1, open: number): LiftPart {
  const mid = S / 2, k = Math.max(0, Math.min(1, open));
  const w = (mid - SW) * (1 - 0.9 * k);
  const u0 = leaf === 0 ? SW : S - SW - w, u1 = u0 + w;
  return { ...shaftBox(s, u0, u1, 0.02, 0.06), y: LIFT_DOOR_H / 2, h: LIFT_DOOR_H, role: 'door' };
}
/** A landing's fixed dressing: the frame round the opening and the light over it. `y` from the storey's floor. */
export function landingParts(s: Shaft): LiftPart[] {
  const at = (u0: number, u1: number, v0: number, v1: number, y0: number, y1: number, role: LiftRole): LiftPart => ({ ...shaftBox(s, u0, u1, v0, v1), y: (y0 + y1) / 2, h: y1 - y0, role });
  return [
    at(0, SW, -0.03, 0.0, 0, LIFT_DOOR_H + 0.14, 'frame'), at(S - SW, S, -0.03, 0.0, 0, LIFT_DOOR_H + 0.14, 'frame'), // jambs, on the side walls' faces
    at(SW, S - SW, -0.03, 0.0, LIFT_DOOR_H, LIFT_DOOR_H + 0.14, 'frame'),                                              // the head
    at(S / 2 - 0.25, S / 2 + 0.25, -0.04, -0.03, LIFT_DOOR_H + 0.05, LIFT_DOOR_H + 0.09, 'call'),                      // the light over the door
  ];
}
/** The call plate: on the jamb nearer the middle of the room, 1.05 m up. `lit` parts glow while the car is coming. */
export function callPlate(s: Shaft): { u: number; x: number; z: number; parts: LiftPart[] } {
  const a = shaftToLocal(s, SW / 2, 0), b = shaftToLocal(s, S - SW / 2, 0);
  const u = Math.hypot(a.x, a.z) <= Math.hypot(b.x, b.z) ? SW / 2 : S - SW / 2;
  const at = (hu: number, v0: number, v1: number, y0: number, y1: number, role: LiftRole): LiftPart => ({ ...shaftBox(s, u - hu, u + hu, v0, v1), y: (y0 + y1) / 2, h: y1 - y0, role });
  const p = shaftToLocal(s, u, -0.05);
  return { u, x: p.x, z: p.z, parts: [at(0.08, -0.05, -0.03, 1.05, 1.37, 'frame'), at(0.055, -0.057, -0.05, 1.2, 1.31, 'ring'), at(0.035, -0.064, -0.057, 1.22, 1.29, 'call')] };
}
/** The boxes (building-local, `y` from the building's ground) of the visit's OWN static lift fixtures on a storey —
 *  slice F's closed `lift_door` and its call panel — which the lift module takes over (it draws working doors). */
export function staticFixtureBoxes(s: Shaft, f: number): Array<{ x0: number; x1: number; y0: number; y1: number; z0: number; z1: number }> {
  const base = f === 0 ? 0 : f * FLOOR_H; // slice F's convention: a fixture's y is measured from the storey's nominal level
  const r = s.rect, e = 0.03;
  const door = shaftBox(s, 0, S, -0.07, 0.07);
  const out = [{ x0: door.x - door.w / 2 - e, x1: door.x + door.w / 2 + e, y0: base - e, y1: base + 2.2 + e, z0: door.z - door.d / 2 - e, z1: door.z + door.d / 2 + e }];
  // the panel: (r.x1 + 0.03, 1.05, r.z0 − 0.2) at an east door; (r.x1 + 0.2, 1.05, r.z0 − 0.03) at a south door
  const px = s.wall === 'west' ? r.x1 + 0.03 : r.x1 + 0.2, pz = s.wall === 'west' ? r.z0 - 0.2 : r.z0 - 0.03;
  out.push({ x0: px - 0.13, x1: px + 0.13, y0: base + 1.05 - e, y1: base + 1.05 + 0.32 + e, z0: pz - 0.13, z1: pz + 0.13 });
  return out;
}

// ─── The picker ──────────────────────────────────────────────────────────────────────────────────────────────────────

export interface PickRow { floor: number; label: string; name: string; here: boolean; enabled: boolean }
/** The floor picker's rows, top storey first; the storey you are on is listed and not offered. */
export function pickerRows(served: ReadonlyArray<number>, here: number): PickRow[] {
  return [...served].sort((a, b) => b - a).map((f) => ({ floor: f, label: floorLabel(f), name: floorName(f), here: f === here, enabled: f !== here }));
}
/** The next storey a press takes in a headset (no picker there): up from here, wrapping to the lowest. */
export function cycleDest(served: ReadonlyArray<number>, here: number, cur: number): number {
  const order = [...served].filter((f) => f !== here).sort((a, b) => a - b);
  if (order.length === 0) return -1;
  const from = cur >= 0 ? cur : here;
  return order.find((f) => f > from) ?? order[0];
}
